import _ from 'lodash'
import BaseSmartInputAutocomplete from "../../../../common/BaseSmartInputAutocomplete";
/**
 * Advanced options config for different options type and different screens
 */

/**
 * Object contains all available operator type that could be String, Array, Number
 * or autocomplete component {@link BaseSmartInputAutocomplete}
 * @type {{NUMBER: string, AUTOCOMPLETE: string, STRING: string, LIST: string}}
 */
const ADVANCED_OPERATOR_TYPE = {
  STRING: 'string',
  NUMBER: 'number',
  LIST: 'list',
  AUTOCOMPLETE: 'autocomplete'
}
/**
 * Object contains all available options
 * @type {{PAGE_TYPE, STATUS, NUMBER, ATTRIBUTE, SCORE, KEYWORD, URL_STATUS, SECTION, URL, COMPANY}}
 */
const ADVANCED_OPTION_TYPE = {
  SECTION: 'section',
  NUMBER: 'number',
  COMPANY: 'company',
  URL: 'url',
  KEYWORD: 'keyword',
  ATTRIBUTE: 'attribute',
  PAGE_TYPE: 'pageType',
  URL_STATUS: 'urlStatus',
  SCORE: 'score',
  DIFF_SCORE: 'diffScore',
  IS_OPTIMIZED: 'isOptimized',
  URL_ATTRIBUTE: 'urlAttribute',
  SERP_FEATURE: 'serpFeature',
  LINKS_TO_MAPPED_URL: 'linksToMappedURL',
  CONTENT_BRIEF_STATUS: 'contentBriefStatus',
  RANKING_URL: 'rankingUrl',
  MISMATCHED_URLS: 'mismatchedUrls',
  RANKING: 'ranking',
  RANK_BLUR_COUNT: 'rankBlurCount',
  GRID_NUMBER: 'gridNumber'
}
/**
 * operator options for STRING operator
 * shown in Operator column
 * defined by Field column
 */
const stringOperators = [
  {
    value: 'equal',
    label: 'Exactly matching'
  }, {
    value: 'contains',
    label: 'Containing'
  }, {
    value: 'starts_with',
    label: 'Begins with'
  }, {
    value: 'ends_with',
    label: 'Ends with'
  }
]
/**
 * operator options for LIST operator
 * shown in Operator column
 * defined by Field column
 */
const listOperators = [
  {
    value: 'equal',
    label: 'Equal to'
  }, {
    value: 'ne',
    label: 'Not Equal'
  }
]
/**
 * operator options for NUMBER operator
 * shown in Operator column
 * defined by Field column
 */
const numberOperators = [
  {
    label: 'Equal to',
    value: 'equal'
  }, {
    label: 'Not Equal',
    value: 'ne'
  }, {
    label: 'Greater Than',
    value: 'gt'
  }, {
    label: 'Less Than',
    value: 'lt'
  }, {
    label: 'Less Than/Equal to',
    value: 'lte'
  }, {
    label: 'Greater Than/Equal to',
    value: 'gte'
  }
]
/**
 * Base config object with all available advanced options types
 * @type {*[]}
 */
const baseFieldsConfig = [
  {
    label: 'Section', // label/value properties for BaseSelect widget
    value: 'section',
    type: ADVANCED_OPTION_TYPE.SECTION, // advanced option type
    selectedOperator: 'equal', // default operator when change field filter
    operators: {
      type: ADVANCED_OPERATOR_TYPE.LIST, // operator type
      operatorVariants: listOperators, // operator variants
      options: [] // options for Filter Value column. Filled after receiving corresponding data from BE
    },
    disableWithoutOptions: true // disable if options don't exist
  }, {
    label: 'Company',
    value: 'company',
    type: ADVANCED_OPTION_TYPE.COMPANY,
    selectedOperator: '',
    operators: {
      type: ADVANCED_OPERATOR_TYPE.LIST,
      operatorVariants: listOperators,
      options: []
    },
    disableWithoutOptions: true
  }, {
    label: 'Mapped URL',
    value: 'url',
    type: ADVANCED_OPTION_TYPE.URL,
    selectedOperator: 'equal',
    operators: {
      type: ADVANCED_OPERATOR_TYPE.LIST,
      operatorVariants: [...listOperators, stringOperators.find(obj => obj.value === 'contains')],
      options: []
    },
    disableWithoutOptions: false
  }, {
    label: 'Ranked URL',
    value: 'rankingUrl',
    type: ADVANCED_OPTION_TYPE.RANKING_URL,
    selectedOperator: 'contains',
    operators: {
      type: ADVANCED_OPERATOR_TYPE.AUTOCOMPLETE,
      operatorVariants: [...stringOperators.slice(0, 2), {
        value: 'not_contains',
        label: 'Not Containing'
      }, ...stringOperators.slice(-2)],
      options: []
    },
    disableWithoutOptions: false
  }, {
    label: 'Keyword',
    value: 'keyword',
    type: ADVANCED_OPTION_TYPE.KEYWORD,
    selectedOperator: 'contains',
    operators: {
      type: ADVANCED_OPERATOR_TYPE.AUTOCOMPLETE,
      operatorVariants: stringOperators,
      options: []
    },
    disableWithoutOptions: false
  }, {
    label: 'Is Optimized',
    value: 'isOptimized',
    type: ADVANCED_OPTION_TYPE.IS_OPTIMIZED,
    selectedOperator: 'equal',
    operators: {
      type: ADVANCED_OPERATOR_TYPE.LIST,
      operatorVariants: listOperators,
      options: []
    },
    disableWithoutOptions: true
  }, {
    label: 'Mismatched URLs',
    value: 'mismatchedUrls',
    type: ADVANCED_OPTION_TYPE.MISMATCHED_URLS,
    selectedOperator: 'equal',
    operators: {
      type: ADVANCED_OPERATOR_TYPE.LIST,
      operatorVariants: listOperators,
      options: []
    },
    disableWithoutOptions: true
  }, {
    label: 'SERP Feature',
    value: 'serpFeature',
    type: ADVANCED_OPTION_TYPE.SERP_FEATURE,
    selectedOperator: 'equal',
    operators: {
      type: ADVANCED_OPERATOR_TYPE.LIST,
      operatorVariants: listOperators,
      options: []
    },
    disableWithoutOptions: true
  }, {
    label: 'URL Attribute',
    value: 'urlAttribute',
    type: ADVANCED_OPTION_TYPE.URL_ATTRIBUTE,
    selectedOperator: 'equal',
    operators: {
      type: ADVANCED_OPERATOR_TYPE.LIST,
      operatorVariants: listOperators,
      options: []
    },
    disableWithoutOptions: true
  }, {
    label: 'Content Brief Status',
    value: 'contentBriefStatus',
    type: ADVANCED_OPTION_TYPE.CONTENT_BRIEF_STATUS,
    selectedOperator: 'equal',
    operators: {
      type: ADVANCED_OPERATOR_TYPE.LIST,
      operatorVariants: listOperators,
      options: []
    },
    disableWithoutOptions: true
  }, {
    label: 'Keyword Attribute',
    value: 'attribute',
    type: ADVANCED_OPTION_TYPE.ATTRIBUTE,
    selectedOperator: 'equal',
    operators: {
      type: ADVANCED_OPERATOR_TYPE.LIST,
      operatorVariants: listOperators,
      options: []
    },
    disableWithoutOptions: true
  }, {
    label: 'Page Type',
    value: 'pageType',
    type: ADVANCED_OPTION_TYPE.PAGE_TYPE,
    selectedOperator: 'equal',
    operators: {
      type: ADVANCED_OPERATOR_TYPE.LIST,
      operatorVariants: listOperators,
      options: []
    },
    disableWithoutOptions: true
  }, {
    label: 'URL Status',
    value: 'urlStatus',
    type: ADVANCED_OPTION_TYPE.URL_STATUS,
    selectedOperator: 'equal',
    operators: {
      type: ADVANCED_OPERATOR_TYPE.LIST,
      operatorVariants: listOperators,
      options: []
    },
    disableWithoutOptions: true
  }, {
    label: 'Rel Score',
    value: 'score',
    type: ADVANCED_OPTION_TYPE.SCORE,
    selectedOperator: 'gte',
    operators: {
      type: ADVANCED_OPERATOR_TYPE.LIST,
      operatorVariants: numberOperators,
      options: []
    },
    disableWithoutOptions: true
  }, {
    label: 'Diff Score',
    value: 'diffScore',
    type: ADVANCED_OPTION_TYPE.DIFF_SCORE,
    selectedOperator: 'gte',
    operators: {
      type: ADVANCED_OPERATOR_TYPE.NUMBER,
      operatorVariants: numberOperators,
      options: []
    },
    disableWithoutOptions: true
  }, {
    label: 'Links to Mapped URL',
    value: 'linksToMappedURL',
    type: ADVANCED_OPTION_TYPE.LINKS_TO_MAPPED_URL,
    selectedOperator: 'gte',
    operators: {
      type: ADVANCED_OPERATOR_TYPE.NUMBER,
      operatorVariants: numberOperators,
      options: []
    },
    disableWithoutOptions: false
  }, {
    label: 'Ranking',
    value: 'ranking',
    type: ADVANCED_OPTION_TYPE.RANKING,
    selectedOperator: 'gte',
    operators: {
      type: ADVANCED_OPERATOR_TYPE.NUMBER,
      operatorVariants: numberOperators,
      options: []
    },
    disableWithoutOptions: true
  }, {
    label: 'Rank Blur Count',
    value: 'rankBlurCount',
    type: ADVANCED_OPTION_TYPE.RANK_BLUR_COUNT,
    selectedOperator: 'gte',
    operators: {
      type: ADVANCED_OPERATOR_TYPE.NUMBER,
      operatorVariants: numberOperators,
      options: []
    },
    disableWithoutOptions: true
  },
  {
    label: 'Grid Number',
    value: 'gridNumber',
    type: ADVANCED_OPTION_TYPE.GRID_NUMBER,
    selectedOperator: 'equal',
    operators: {
      type: ADVANCED_OPERATOR_TYPE.NUMBER,
      operatorVariants: numberOperators,
      options: []
    },
    disableWithoutOptions: true
  },

]

function customizeConfig(properties = []) {
  return _.cloneDeep(baseFieldsConfig).filter(item => properties.includes(item.type))
}

/**
 * config for specific screens
 */
const competitionFieldsConfig = customizeConfig([
  // ADVANCED_OPTION_TYPE.COMPANY,
  ADVANCED_OPTION_TYPE.ATTRIBUTE,
  ADVANCED_OPTION_TYPE.SECTION,
  ADVANCED_OPTION_TYPE.SCORE,
  ADVANCED_OPTION_TYPE.DIFF_SCORE,
  ADVANCED_OPTION_TYPE.URL,
  ADVANCED_OPTION_TYPE.RANKING_URL,
  ADVANCED_OPTION_TYPE.KEYWORD,
  ADVANCED_OPTION_TYPE.PAGE_TYPE,
  ADVANCED_OPTION_TYPE.URL_STATUS,
  ADVANCED_OPTION_TYPE.IS_OPTIMIZED,
  ADVANCED_OPTION_TYPE.URL_ATTRIBUTE,
  ADVANCED_OPTION_TYPE.SERP_FEATURE,
  ADVANCED_OPTION_TYPE.LINKS_TO_MAPPED_URL,
  ADVANCED_OPTION_TYPE.CONTENT_BRIEF_STATUS,
  ADVANCED_OPTION_TYPE.RANKING,
  ADVANCED_OPTION_TYPE.RANK_BLUR_COUNT])

const contentBriefCompetitionFieldsConfig = customizeConfig([
  ADVANCED_OPTION_TYPE.ATTRIBUTE,
  ADVANCED_OPTION_TYPE.SCORE,
  ADVANCED_OPTION_TYPE.DIFF_SCORE,
  ADVANCED_OPTION_TYPE.RANKING_URL,
  ADVANCED_OPTION_TYPE.KEYWORD,
  ADVANCED_OPTION_TYPE.SERP_FEATURE,
  ADVANCED_OPTION_TYPE.RANKING,
  ADVANCED_OPTION_TYPE.RANK_BLUR_COUNT])

const urlFieldsConfig = customizeConfig([
  ADVANCED_OPTION_TYPE.URL,
  ADVANCED_OPTION_TYPE.RANKING_URL,
  ADVANCED_OPTION_TYPE.KEYWORD,
  ADVANCED_OPTION_TYPE.ATTRIBUTE,
  ADVANCED_OPTION_TYPE.SECTION,
  ADVANCED_OPTION_TYPE.SCORE,
  ADVANCED_OPTION_TYPE.DIFF_SCORE,
  ADVANCED_OPTION_TYPE.URL_STATUS,
  ADVANCED_OPTION_TYPE.PAGE_TYPE,
  ADVANCED_OPTION_TYPE.IS_OPTIMIZED,
  ADVANCED_OPTION_TYPE.URL_ATTRIBUTE,
  ADVANCED_OPTION_TYPE.SERP_FEATURE,
  ADVANCED_OPTION_TYPE.LINKS_TO_MAPPED_URL,
  ADVANCED_OPTION_TYPE.CONTENT_BRIEF_STATUS,
  ADVANCED_OPTION_TYPE.MISMATCHED_URLS,
  ADVANCED_OPTION_TYPE.RANK_BLUR_COUNT
])

const productGridFieldsConfig = customizeConfig([
  // ADVANCED_OPTION_TYPE.URL,
  // ADVANCED_OPTION_TYPE.RANKING_URL,
  ADVANCED_OPTION_TYPE.KEYWORD,
  ADVANCED_OPTION_TYPE.ATTRIBUTE,
  ADVANCED_OPTION_TYPE.SECTION,
  ADVANCED_OPTION_TYPE.GRID_NUMBER,
  // ADVANCED_OPTION_TYPE.SCORE,
  // ADVANCED_OPTION_TYPE.DIFF_SCORE,
  // ADVANCED_OPTION_TYPE.URL_STATUS,
  ADVANCED_OPTION_TYPE.PAGE_TYPE,
  // ADVANCED_OPTION_TYPE.IS_OPTIMIZED,
  ADVANCED_OPTION_TYPE.URL_ATTRIBUTE,
  // ADVANCED_OPTION_TYPE.SERP_FEATURE,
  // ADVANCED_OPTION_TYPE.LINKS_TO_MAPPED_URL,
  // ADVANCED_OPTION_TYPE.CONTENT_BRIEF_STATUS,
  // ADVANCED_OPTION_TYPE.MISMATCHED_URLS,
  // ADVANCED_OPTION_TYPE.RANK_BLUR_COUNT
])

const progressFieldsConfig = customizeConfig([
  ADVANCED_OPTION_TYPE.URL,
  ADVANCED_OPTION_TYPE.RANKING_URL,
  ADVANCED_OPTION_TYPE.KEYWORD,
  ADVANCED_OPTION_TYPE.ATTRIBUTE,
  ADVANCED_OPTION_TYPE.SECTION,
  ADVANCED_OPTION_TYPE.SCORE,
  ADVANCED_OPTION_TYPE.DIFF_SCORE,
  ADVANCED_OPTION_TYPE.URL_STATUS,
  ADVANCED_OPTION_TYPE.PAGE_TYPE,
  ADVANCED_OPTION_TYPE.IS_OPTIMIZED,
  ADVANCED_OPTION_TYPE.URL_ATTRIBUTE,
  ADVANCED_OPTION_TYPE.SERP_FEATURE,
  ADVANCED_OPTION_TYPE.LINKS_TO_MAPPED_URL,
  ADVANCED_OPTION_TYPE.CONTENT_BRIEF_STATUS,
  ADVANCED_OPTION_TYPE.MISMATCHED_URLS,
  ADVANCED_OPTION_TYPE.RANK_BLUR_COUNT
])
const contentBriefFieldsConfig = customizeConfig([
  ADVANCED_OPTION_TYPE.SCORE,
  ADVANCED_OPTION_TYPE.DIFF_SCORE,
  ADVANCED_OPTION_TYPE.SERP_FEATURE,
  ADVANCED_OPTION_TYPE.MISMATCHED_URLS,
  ADVANCED_OPTION_TYPE.RANK_BLUR_COUNT
])

const statusFieldsConfig = customizeConfig([
  ADVANCED_OPTION_TYPE.SECTION,
  ADVANCED_OPTION_TYPE.URL,
  ADVANCED_OPTION_TYPE.RANKING_URL,
  ADVANCED_OPTION_TYPE.KEYWORD,
  ADVANCED_OPTION_TYPE.ATTRIBUTE,
  ADVANCED_OPTION_TYPE.SCORE,
  ADVANCED_OPTION_TYPE.DIFF_SCORE,
  ADVANCED_OPTION_TYPE.URL_STATUS,
  ADVANCED_OPTION_TYPE.PAGE_TYPE,
  ADVANCED_OPTION_TYPE.IS_OPTIMIZED,
  ADVANCED_OPTION_TYPE.URL_ATTRIBUTE,
  ADVANCED_OPTION_TYPE.SERP_FEATURE,
  ADVANCED_OPTION_TYPE.LINKS_TO_MAPPED_URL,
  ADVANCED_OPTION_TYPE.CONTENT_BRIEF_STATUS,
  ADVANCED_OPTION_TYPE.MISMATCHED_URLS,
  ADVANCED_OPTION_TYPE.RANK_BLUR_COUNT
])

const attributesFieldsConfig = customizeConfig([
  ADVANCED_OPTION_TYPE.URL,
  ADVANCED_OPTION_TYPE.KEYWORD,
  ADVANCED_OPTION_TYPE.SECTION,
  ADVANCED_OPTION_TYPE.SCORE,
  ADVANCED_OPTION_TYPE.URL_STATUS,
  ADVANCED_OPTION_TYPE.PAGE_TYPE,
  ADVANCED_OPTION_TYPE.IS_OPTIMIZED,
  ADVANCED_OPTION_TYPE.SERP_FEATURE,
  ADVANCED_OPTION_TYPE.LINKS_TO_MAPPED_URL,
  ADVANCED_OPTION_TYPE.CONTENT_BRIEF_STATUS,
  ADVANCED_OPTION_TYPE.RANK_BLUR_COUNT
])

const keywordRankingReportFieldsConfig = customizeConfig([
  ADVANCED_OPTION_TYPE.URL,
  ADVANCED_OPTION_TYPE.RANKING_URL,
  ADVANCED_OPTION_TYPE.KEYWORD,
  ADVANCED_OPTION_TYPE.SECTION,
  ADVANCED_OPTION_TYPE.SCORE,
  ADVANCED_OPTION_TYPE.DIFF_SCORE,
  ADVANCED_OPTION_TYPE.ATTRIBUTE,
  ADVANCED_OPTION_TYPE.URL_ATTRIBUTE,
  ADVANCED_OPTION_TYPE.URL_STATUS,
  ADVANCED_OPTION_TYPE.PAGE_TYPE,
  ADVANCED_OPTION_TYPE.IS_OPTIMIZED,
  ADVANCED_OPTION_TYPE.SERP_FEATURE,
  ADVANCED_OPTION_TYPE.LINKS_TO_MAPPED_URL,
  ADVANCED_OPTION_TYPE.CONTENT_BRIEF_STATUS,
  ADVANCED_OPTION_TYPE.MISMATCHED_URLS,
  ADVANCED_OPTION_TYPE.RANK_BLUR_COUNT
])
const sectionFieldsConfig = _.cloneDeep(statusFieldsConfig)
/**
 * function that fills operators.options array by received form BE options
 * @param config - baseFieldsConfig
 * @param stateProperty - options received form BE
 * @param optionType - corresponding ADVANCED_OPTION_TYPE
 */
const addOptionsToConfig = function (config, stateProperty, optionType) {
  const options = stateProperty.map(item => {
    // this value is used as "value" query in request
    item.value = item._id
    return item
  })
  const optionList = config.find(item => item.type === optionType)
  optionList.operators.options = options
}
/**
 * unction that converts created advanced option list into  query params
 * @param options
 * @returns {*}
 */
const convertAdvancedOptionsToFilters = function (options) {
  return options.map((item) => {
    return {
      property_name: item.selectedField,
      value: _.get(item, 'selectedFilter.value'),
      operator: _.get(item, 'selectedOperator')
    }
  })
}

export {
  ADVANCED_OPERATOR_TYPE,
  ADVANCED_OPTION_TYPE,
  competitionFieldsConfig,
  urlFieldsConfig,
  productGridFieldsConfig,
  progressFieldsConfig,
  statusFieldsConfig,
  sectionFieldsConfig,
  baseFieldsConfig,
  stringOperators,
  contentBriefFieldsConfig,
  contentBriefCompetitionFieldsConfig,
  attributesFieldsConfig,
  keywordRankingReportFieldsConfig,
  addOptionsToConfig,
  convertAdvancedOptionsToFilters
}
